import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import liveChatLogo from '../static/images/imageChat.png';
import { Helmet } from 'react-helmet';
import CustomerPage from '../../homeDashboard/templates/CustomerPage';
import DriverPage from '../../driverDashboard/template/DriverPage';
import tripLogo from '../../logos/storage-sprint.png';
import { fetchUserTrips, submitSupportRequest } from "../../Services/ApiCallsServices/SupportApi";
import { useAuth } from "../../context/AuthContext";
import moment from "moment";


const SupportPage = () => {
  const [visibleFAQ, setVisibleFAQ] = useState(null);
  const [showAllFAQs, setShowAllFAQs] = useState(false);
  const [trips, setTrips] = useState([]);
  const [showAllTrips, setShowAllTrips] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [helpType, setHelpType] = useState('');
  const [description, setDescription] = useState('');
  const [role, setRole] = useState(null);
  const navigate = useNavigate();
  const { auth } = useAuth();


  useEffect(() => {
    const roleData = auth.user?.role || '';
    setRole(roleData);
  }, []);

  useEffect(() => {
    if (!auth.user?.email || !role) return;

    const getTrips = async () => {
      try {
        const email = auth.user.email;
        const tripsData = await fetchUserTrips(email, role);
        setTrips(tripsData.trips || []);
      } catch (error) {
        console.error("Error fetching trips:", error.message);
      }
    };

    getTrips();
  }, [role, auth.user?.email]);


  const toggleFAQ = (index) => {
    setVisibleFAQ(prevIndex => (prevIndex === index ? null : index));
  };

  const handleLiveChat = () => {
    navigate('/chat/');
  };

  const toggleAllFAQs = () => {
    setShowAllFAQs(prev => !prev);
  };

  const toggleAllTrips = () => {
    setShowAllTrips(prev => !prev);
  };

  const handleSelectTrip = (trip) => {
    setSelectedTrip(trip);
    setHelpType('');
    setDescription('');
  };

  const handleSubmitSupportRequest = async () => {
    if (!helpType || !description) {
      alert("Please select a help type and provide a description.");
      return;
    }

    try {
      const payload = {
        reference: selectedTrip.reference,
        help_type: helpType,
        description: description,
      };

      await submitSupportRequest(payload);
      alert('Support request submitted successfully!');
      setSelectedTrip(null);
      setHelpType('');
      setDescription('');
    } catch (error) {
      console.error('Error submitting support request:', error);
      alert('Error submitting support request.');
    }
  };

  if (!role) return <div className="p-4">Loading...</div>;

  const MasterComponent = role === 'driver' || role === 'admin' ? DriverPage : CustomerPage;

  return (
    <MasterComponent>
      <div className="space-y-6 p-4">
        <Helmet>
          <title>Support - Storage Sprint</title>
        </Helmet>

        {/* Header Section */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <header className="text-center">
             <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                <span className="text-yellow-500">Support</span> Center
            </h1>
            <h2 className="text-gray-600 mt-2">How can we help you today?</h2>
          </header>
        </div>

        {/* Trip Selection Section */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-semibold text-gray-800 pb-2 border-b border-gray-200 relative">
            <span className="after:absolute after:bottom-0 after:left-0 after:w-12 after:h-0.5 after:bg-yellow-400">
              Your Trips
            </span>
          </h3>
          <div className="mt-4 space-y-3">
            {trips.slice(0, showAllTrips ? trips.length : 2).map((trip) => (
              <div
                key={trip.reference}
                className={`flex items-center p-4 rounded-lg border cursor-pointer transition-all ${
                  selectedTrip?.reference === trip.reference 
                    ? 'border-yellow-400 bg-yellow-50' 
                    : 'border-gray-200 hover:border-yellow-300 hover:-translate-y-0.5'
                }`}
                onClick={() => handleSelectTrip(trip)}
              >
                <img src={tripLogo} alt="Trip" className="w-12 h-12 mr-4" />
                <div className="flex-1">
                  <p className="font-medium text-gray-800">
                    {moment(trip.date).format('Do MMMM YYYY, h:mm A')}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">From:</span> {trip.pickupLocation}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">To:</span> {trip.dropoffLocation}
                  </p>
                  <p className="font-semibold text-gray-800 mt-1">R{trip.driverAmount}</p>
                </div>
              </div>
            ))}
          </div>
          {trips.length > 2 && (
            <button 
              onClick={toggleAllTrips}
              className="mt-4 text-yellow-600 font-semibold hover:underline focus:outline-none"
            >
              {showAllTrips ? 'Show Less' : 'Show All Trips'}
            </button>
          )}
        </div>

        {/* Support Form Section */}
        {selectedTrip && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-800 pb-2 border-b border-gray-200 relative">
              <span className="after:absolute after:bottom-0 after:left-0 after:w-12 after:h-0.5 after:bg-yellow-400">
                Support Request
              </span>
            </h3>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Help Type</label>
                <select 
                  value={helpType} 
                  onChange={(e) => setHelpType(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 outline-none transition"
                >
                  <option value="">Select Help Type</option>
                  <option value="fare-review">Fare Review</option>
                  <option value="damaged-item">Damaged Item</option>
                  <option value="lost-item">Lost Item</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {helpType && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Please describe your issue in detail..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 outline-none transition min-h-[120px]"
                  />
                </div>
              )}

              {helpType && description && (
                <button 
                  onClick={handleSubmitSupportRequest}
                  className="px-6 py-3 bg-yellow-400 text-gray-800 font-semibold rounded-lg hover:bg-yellow-500 transition focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2"
                >
                  Submit Request
                </button>
              )}
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-semibold text-gray-800 pb-2 border-b border-gray-200 relative">
            <span className="after:absolute after:bottom-0 after:left-0 after:w-12 after:h-0.5 after:bg-yellow-400">
              Frequently Asked Questions
            </span>
          </h3>
          <div className="mt-4 space-y-2">
            <div 
              className={`border rounded-lg overflow-hidden transition ${
                visibleFAQ === 0 ? 'border-yellow-400' : 'border-gray-200'
              }`}
              onClick={() => toggleFAQ(0)}
            >
              <div className="flex justify-between items-center p-4 hover:bg-gray-50 cursor-pointer">
                <h4 className="font-medium text-gray-800">Can I schedule a ride in advance?</h4>
                <span className="text-yellow-500 font-bold text-lg">
                  {visibleFAQ === 0 ? '−' : '+'}
                </span>
              </div>
              {visibleFAQ === 0 && (
                <div className="p-4 pt-0 text-gray-600">
                  Yes, you can schedule a ride in advance on Storage Sprint.
                </div>
              )}
            </div>

            <div 
              className={`border rounded-lg overflow-hidden transition ${
                visibleFAQ === 1 ? 'border-yellow-400' : 'border-gray-200'
              }`}
              onClick={() => toggleFAQ(1)}
            >
              <div className="flex justify-between items-center p-4 hover:bg-gray-50 cursor-pointer">
                <h4 className="font-medium text-gray-800">What should I do if I left something in the car?</h4>
                <span className="text-yellow-500 font-bold text-lg">
                  {visibleFAQ === 1 ? '−' : '+'}
                </span>
              </div>
              {visibleFAQ === 1 && (
                <div className="p-4 pt-0 text-gray-600">
                  Contact the driver immediately through the app's messaging system.
                </div>
              )}
            </div>

            {showAllFAQs && (
              <>
                <div 
                  className={`border rounded-lg overflow-hidden transition ${
                    visibleFAQ === 2 ? 'border-yellow-400' : 'border-gray-200'
                  }`}
                  onClick={() => toggleFAQ(2)}
                >
                  <div className="flex justify-between items-center p-4 hover:bg-gray-50 cursor-pointer">
                    <h4 className="font-medium text-gray-800">How do I use the app?</h4>
                    <span className="text-yellow-500 font-bold text-lg">
                      {visibleFAQ === 2 ? '−' : '+'}
                    </span>
                  </div>
                  {visibleFAQ === 2 && (
                    <div className="p-4 pt-0 text-gray-600">
                      You can schedule rides, view trip history, and contact support through the app.
                    </div>
                  )}
                </div>

                <div 
                  className={`border rounded-lg overflow-hidden transition ${
                    visibleFAQ === 3 ? 'border-yellow-400' : 'border-gray-200'
                  }`}
                  onClick={() => toggleFAQ(3)}
                >
                  <div className="flex justify-between items-center p-4 hover:bg-gray-50 cursor-pointer">
                    <h4 className="font-medium text-gray-800">How do I request a ride?</h4>
                    <span className="text-yellow-500 font-bold text-lg">
                      {visibleFAQ === 3 ? '−' : '+'}
                    </span>
                  </div>
                  {visibleFAQ === 3 && (
                    <div className="p-4 pt-0 text-gray-600">
                      Select your pickup and drop-off locations, then confirm your ride request in the app.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <button 
            onClick={toggleAllFAQs}
            className="mt-4 text-yellow-600 font-semibold hover:underline focus:outline-none"
          >
            {showAllFAQs ? 'Show Less FAQs' : 'Show More FAQs'}
          </button>

          {showAllFAQs && (
            <div className="mt-6 pt-4 border-t border-gray-200 text-center">
              <p className="text-gray-600 mb-3">Still need help?</p>
              <button 
                onClick={handleLiveChat}
                className="inline-flex items-center px-5 py-2.5 bg-yellow-400 text-gray-800 font-semibold rounded-lg hover:bg-yellow-500 transition focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2"
              >
                <img src={liveChatLogo} alt="Chat" className="w-5 h-5 mr-2" />
                Start Live Chat
              </button>
            </div>
          )}
        </div>
      </div>
    </MasterComponent>
  );
};

export default SupportPage;